import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { BasicContactPerson, CGV2LocationOutDTO, LocationPreviewV4OutDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { Field } from '@components/framework/formfield/Field.tsx';
import ReadonlyInput from '@components/framework/input/ReadonlyInput/ReadonlyInput.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import { formatAddress } from '@utils/formators.tsx';
import InputContact from '@components/framework/InputContact/InputContact.tsx';
import { MultiSelect } from 'primereact/multiselect';
import { isNotBlank, isNotNullOrUndefined } from '@utils/utils.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import LocationSelect from '@components/framework/locationselect/LocationSelect.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import SvgCoButtonDelete from '@icons/CoButtonDelete.tsx';
import { findValidationMessage } from '@utils/validation.tsx';

interface ComponentProps {
  applicationId: string;
  expeditionId: string;
  companyLocationId: string;
  locationId: string;
  onContinue: () => void;
  location: LocationPreviewV4OutDTO;
  /** RD-2945: the stop may be swapped for another list location (NEW expedition, no SN, no reservation). */
  canChangeLocation?: boolean;
}

interface EditExpeditionLocationForm {
  companyLocationId: string;
  loadingReference?: string;
  selectedPerson?: BasicContactPerson;
  additionalContacts?: BasicContactPerson[];
  additionalPersons?: BasicContactPerson[];
}

export const useCreateEditExpeditionLocation = () => {
  const [visible, setVisible] = useState(false);

  const EditExpeditionLocationSidebar: React.FC<ComponentProps> = ({ onContinue, applicationId, companyLocationId, locationId, expeditionId, location, canChangeLocation = false }) => {
    const { tr } = useTranslationLgs();
    const { getOneCGLocationV2, editCompanyLocationExpedition } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loc, setLoc] = useState<CGV2LocationOutDTO>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (isNotNullOrUndefined(companyLocationId)) {
        getOneCGLocationV2(companyLocationId, d => {
          setLoc(d);
          const isTypedIn = (person: BasicContactPerson) =>
            isNotBlank(person.contactEmail) && !d.contacts?.some(contact => contact.contactEmail === person.contactEmail);
          form.useProduce(draft => {
            draft.additionalContacts = (location.additionalContacts || []).filter(person => !isTypedIn(person));
            draft.additionalPersons = (location.additionalContacts || []).filter(isTypedIn);
          });
        });
      }
    }, [companyLocationId]);

    const mapPerson = (p: BasicContactPerson) => {
      return {
        contactEmail: p?.contactEmail || '',
        contactName: p?.contactName || '',
        contactPhone: p?.contactPhone || '',
      };
    };

    const save = (data: EditExpeditionLocationForm) => {
      setLoading(true);
      editCompanyLocationExpedition(
        expeditionId,
        locationId,
        {
          companyLocationId: data.companyLocationId,
          selectedPerson: data.selectedPerson ? mapPerson(data.selectedPerson) : null,
          additionalContacts: [...(data.additionalContacts || []), ...(data.additionalPersons || [])].map(t => mapPerson(t)),
          loadingReference: data.loadingReference,
        },
        () => {
          setLoading(false);
          onContinue();
        },
        null,
        {
          onValidationFailed: errors => {
            setBeValidationErrors(errors);
            setLoading(false);
          },
          onError: (_error, showStandardError) => {
            showStandardError();
            setLoading(false);
          },
        },
      );
    };

    const schema = yup.object().shape({
      additionalPersons: yup.array().of(
        yup.object().shape({
          contactEmail: yup.string().email(tr(`validation.valid_email`, 'Value has to be valid email address.')).required(),
        }),
      ),
    });
    const form = useForm<EditExpeditionLocationForm>(
      schema,
      {
        companyLocationId: companyLocationId,
        additionalContacts: location.additionalContacts,
        loadingReference: location.loadingReference,
        selectedPerson: location.selectedPerson,
      },
      d => save(d),
      false,
      false,
    );

    // Same prefill as the create wizard (RouteLocationCard): first contact becomes the main one,
    // contacts flagged for automatic notification go to the notify list, loading reference is kept.
    const onLocationChange = (v: CGV2LocationOutDTO) => {
      const firstContact = v.contacts?.find((_, index) => index == 0);
      setLoc({ ...v, contacts: v.contacts?.filter(contact => isNotBlank(contact.contactEmail)) || [] });
      form.useProduce(draft => {
        draft.companyLocationId = v.locationId;
        draft.selectedPerson = isNotNullOrUndefined(firstContact)
          ? {
              contactEmail: firstContact.contactEmail?.trim() || null,
              contactName: firstContact.contactName?.trim() || null,
              contactPhone: firstContact.contactPhone?.trim() || null,
            }
          : null;
        draft.additionalContacts =
          v.contacts?.filter(contact => isNotNullOrUndefined(contact.contactEmail)).filter(contact => contact.automaticPrefillToNotify) || [];
      });
    };

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('EditLocationAndContactSidebar.zmenaNaLokalite', 'Změna na lokalitě')}
          loading={loading}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <VerticalFieldAndValueWithIcon
              label={tr(`AddMoreCarriers.forExpedition`, `For expedition`)}
              valueText={applicationId}
              iconPath={'/images/icons/ringil3/forExpedition.svg'}
            />

            {isNotNullOrUndefined(loc) && (
              <>
                <Field label={tr('EditLocationAndContactSidebar.lokace', 'Lokace')}>
                  {canChangeLocation ? (
                    <LocationSelect fullWidth value={form.values.companyLocationId} onChange={onLocationChange} dataTestId={'editLocationSelect'} />
                  ) : (
                    <ReadonlyInput value={loc.addressTemplate.name} />
                  )}
                </Field>

                <Field label={tr('EditLocationAndContactSidebar.adresa', 'Adresa')}>
                  <ReadonlyInput
                    value={formatAddress(loc.addressTemplate.country, loc.addressTemplate.postalCode, loc.addressTemplate.city, loc.addressTemplate.streetNr)}
                  />
                </Field>

                <COInlineMessage variant={'info'} icon={<SvgWarningExclamation />}>
                  Seznam kontaktních osob je součástí vybrané lokace. Změnu můžete provést v jejím nastavení.
                </COInlineMessage>

                <Field label={tr('EditLocationAndContactSidebar.hlavniKontakt', 'Hlavní kontakt')}>
                  <InputContact
                    contacts={loc?.contacts}
                    value={form.values.selectedPerson}
                    onChange={v => {
                      form.useProduce(draft => {
                        draft.selectedPerson = v;
                      });
                    }}
                    fullWidth
                  />
                </Field>
                <Field label={tr('EditLocationAndContactSidebar.notifikovatOPreprave', 'Notifikovat o preprave')}>
                  <MultiSelect
                    options={loc?.contacts}
                    optionLabel={'contactName'}
                    dataKey="contactEmail"
                    value={form.values.additionalContacts}
                    onChange={e => {
                      form.useProduce(draft => {
                        draft.additionalContacts = e.value;
                      });
                    }}
                  />
                </Field>
                {form.values.additionalPersons?.map((person, personIndex) => {
                  return (
                    <React.Fragment key={personIndex}>
                      <div className={'grid grid-cols-2 gap-4'}>
                        <Field label={tr('CreateExpedition.jmenoOsoby', 'Name of person')} required>
                          <RingilInputText
                            value={person.contactName}
                            dataTestId={`editLocationNotify-additional-${personIndex}-name`}
                            onChange={v =>
                              form.useProduce(draft => {
                                draft.additionalPersons[personIndex].contactName = v;
                              })
                            }
                            fullWidth
                          />
                        </Field>
                        <Field
                          label={tr('CreateExpedition.notifikovatEmailemOPreprave', 'Notify by email')}
                          required
                          errorMessage={findValidationMessage(form.validationErrors, `additionalPersons[${personIndex}].contactEmail`, tr)}
                          actionButtons={
                            <RingilButton
                              mode={'text-only'}
                              size={'small'}
                              icon={<SvgCoButtonDelete />}
                              label=""
                              dataTestId={`editLocationNotify-additional-${personIndex}-delete`}
                              onClick={() => {
                                form.useProduce(draft => {
                                  draft.additionalPersons = draft.additionalPersons.filter((_, i) => i !== personIndex);
                                });
                              }}
                            />
                          }
                        >
                          <RingilInputText
                            value={person.contactEmail}
                            dataTestId={`editLocationNotify-additional-${personIndex}-email`}
                            onChange={v =>
                              form.useProduce(draft => {
                                draft.additionalPersons[personIndex].contactEmail = v;
                              })
                            }
                            fullWidth
                          />
                        </Field>
                      </div>
                    </React.Fragment>
                  );
                })}
                <LinkWithIcon
                  label={tr('CreateExpedition.notifikovatDalsiEmail', 'Notify another email')}
                  icon={<SvgIconPlus />}
                  dataTestId={'editLocationNotify-addEmail'}
                  onClick={() => {
                    form.useProduce(draft => {
                      if (draft.additionalPersons == null) {
                        draft.additionalPersons = [];
                      }
                      draft.additionalPersons.push({ contactName: '', contactEmail: '' });
                    });
                  }}
                ></LinkWithIcon>
                <Field label={tr('EditLocationAndContactSidebar.referenceProNakladku', 'Reference pro nakládku')}>
                  <RingilInputText className="w-full" value={form.values.loadingReference} onChange={v => form.setFieldValue('loadingReference', v)} />
                </Field>
              </>
            )}
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { EditExpeditionLocationSidebar, setVisible, visible };
};
